import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";
import { CartProvider, useCart } from "./context/CartContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import Cart from "./pages/Cart";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Account from "./pages/Account";
import Profile from "./pages/Profile";
import Addresses from "./pages/Addresses";
import Orders from "./pages/Orders";
import OrderDetail from "./pages/OrderDetail";
import Checkout from "./pages/Checkout";
import GuestCheckout from "./pages/GuestCheckout";
import Thanks from "./pages/Thanks";
import ProductDetail from "./pages/ProductDetail";
import About from "./pages/About";
import Blog from "./pages/Blog";
import Contact from "./pages/Contact";
import Careers from "./pages/Careers";
import Press from "./pages/Press";
import Shipping from "./pages/Shipping";
import FAQ from "./pages/FAQ";
import CareGuide from "./pages/CareGuide";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Cookies from "./pages/Cookies";
import InfoCard from "./pages/InfoCard";
import NotFound from "./pages/NotFound";
import AdminRoute from "./components/AdminRoute";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminOrderDetail from "./pages/admin/AdminOrderDetail";
import AdminProducts from "./pages/admin/AdminProducts";
import ProductEdit from "./pages/admin/ProductEdit";
import AdminCollections from "./pages/admin/AdminCollections";
import CollectionEdit from "./pages/admin/CollectionEdit";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminStock from "./pages/admin/AdminStock";
import AdminInquiries from "./pages/admin/AdminInquiries";
import "./index.css";

function AppRoutes() {
  const location = useLocation();
  const { cartCount, addToCart, refreshServerCart } = useCart();

  const handleAddToCart = (product) => {
    addToCart({
      id: product.id,
      productId: product.id,
      name: product.name,
      price: product.price,
      length: Number(product.length) || 0,
      width: Number(product.width) || 0,
      height: Number(product.height) || 0,
      colorHex: product.color,
      imageUrl: product.imageUrl,
    }).catch(() => {});
  };

  return (
    <>
      <Navbar cartCount={cartCount} />
      <main className={location.pathname === '/' ? '' : 'page-nav-padding'}>
        <Routes>
        <Route path="/" element={<Home onAddToCart={handleAddToCart} />} />
        <Route path="/shop" element={<Shop onAddToCart={handleAddToCart} />} />
        <Route path="/shop/:id" element={<Shop onAddToCart={handleAddToCart} />} />
        <Route path="/collections" element={<Shop onAddToCart={handleAddToCart} />} />
        <Route path="/collections/:id" element={<Shop onAddToCart={handleAddToCart} />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/about" element={<About />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/press" element={<Press />} />
        <Route path="/shipping" element={<Shipping />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/care" element={<CareGuide />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/info-card" element={<InfoCard />} />
        <Route path="/info" element={<InfoCard />} />
        <Route
          path="/product/:slug"
          element={<ProductDetail onCartUpdated={refreshServerCart} />}
        />

        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/addresses"
          element={
            <ProtectedRoute>
              <Addresses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/orders"
          element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/orders/:id"
          element={
            <ProtectedRoute>
              <OrderDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          }
        />
        <Route path="/guest-checkout" element={<GuestCheckout />} />
        <Route path="/thanks" element={<Thanks onCartUpdated={refreshServerCart} />} />

        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="orders/:id" element={<AdminOrderDetail />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="products/:id" element={<ProductEdit />} />
          <Route path="collections" element={<AdminCollections />} />
          <Route path="collections/:id" element={<CollectionEdit />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="stock" element={<AdminStock />} />
          <Route path="inquiries" element={<AdminInquiries />} />
        </Route>

        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!location.pathname.startsWith("/admin") && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <BrowserRouter>
          <LanguageProvider>
            <ThemeProvider>
              <CartProvider>
                <ScrollToTop />
                <AppRoutes />
              </CartProvider>
            </ThemeProvider>
          </LanguageProvider>
        </BrowserRouter>
      </AuthProvider>
    </ToastProvider>
  );
}


